import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
    Box,
    Typography,
    Paper,
    Button,
    IconButton,
    Divider,
    Card,
    CardContent,
    CircularProgress,
    Alert,
    useTheme,
    alpha,
    Tabs,
    Tab,
    TextField,
    Menu,
    MenuItem,
    ListItemIcon,
    ListItemText,
} from '@mui/material';
import {
    ArrowBack as ArrowBackIcon,
    Send as SendIcon,
    Refresh as RefreshIcon,
    Code as VariableIcon,
    KeyboardArrowDown as ArrowDownIcon,
    Sync as SyncIcon,
} from '@mui/icons-material';
import promptService, { Prompt } from '../../../../services/prompt.service';

function TabPanel(props: any) {
    const { children, value, index, ...other } = props;

    return (
        <div
            role="tabpanel"
            hidden={value !== index}
            id={`vertical-tabpanel-${index}`}
            aria-labelledby={`vertical-tab-${index}`}
            style={{ width: "80%" }}
            {...other}
        >
            {value === index && (
                <Box sx={{ p: 3 }}>
                    <Typography>{children}</Typography>
                </Box>
            )}
        </div>
    );
}

const Prompts: React.FC = () => {
    const { id } = useParams<{ id: string }>();
    const navigate = useNavigate();
    const theme = useTheme();

    // State variables
    const [defaultPrompt, setDefaultPrompt] = useState<any>('');
    const [selectedPromptId, setSelectedPromptId] = useState<string>('');
    const [promptSlug, setPromptSlug] = useState<string>('');
    const [selectedPromptType, setSelectedPromptType] = useState<string>('');

    // Prompt states
    const [prompt, setPrompt] = useState<string>('');
    const [prompts, setPrompts] = useState<Prompt[]>([]);
    const [promptLoading, setPromptLoading] = useState<boolean>(false);
    const [promptsLoading, setPromptsLoading] = useState<boolean>(false);

    const [syncLoading, setSyncLoading] = useState(false);
    const [promptsError, setPromptsError] = useState<string | null>(null);

    // Variable menu states
    const [variableAnchorEl, setVariableAnchorEl] = useState<null | HTMLElement>(null);
    const textFieldRef = React.useRef<HTMLInputElement>(null);

    // Get default prompt list (kept for reference, but primary data is from jobSeekerPrompts)
    const getDefaultPromptList = async () => {
        try {
            const response = await promptService.getDefaultPromptList();
            setDefaultPrompt(response?.data);
        } catch (err: any) {
            console.error('Error fetching default prompts:', err);
        }
    };

    useEffect(() => {
        fetchJobSeekerPrompts();
        getDefaultPromptList();
    }, []);

    // Handle tab change
    const handleChangePromptTab = (_event: any, newValue: any) => {
        setSelectedPromptId(newValue);
        const selectedPrompt = prompts.find(p => p._id === newValue);
        if (selectedPrompt) {
            setPrompt(selectedPrompt.prompt || '');
            setPromptSlug(selectedPrompt.braintrust_slug || ''); // assuming braintrust_slug exists on the prompt object
        }
    };

    // Handle prompt submission
    const handlePromptSubmit = async () => {
        if (!process.env.NODE_ENV || process.env.NODE_ENV === 'development') {
            // console.log("Saving prompt:", { selectedPromptId, prompt });
        }

        setPromptLoading(true);

        try {
            const payload = {
                "id": selectedPromptId,
                "prompt": prompt,
                "type": selectedPromptId,
                "company_id": "",
                "project_id": "",
                "round_id": ""
            }
            await promptService.editPrompt(payload);
            showSnackbar('Prompt saved successfully', 'success');
            fetchJobSeekerPrompts();
        } catch (err: any) {
            console.error('Error adding prompt:', err);
            showSnackbar('Failed to save prompt', 'error');
        } finally {
            setPromptLoading(false);
        }
    };

    // Fetch company prompts
    const fetchJobSeekerPrompts = async () => {
        setPromptsError(null);
        setPromptsLoading(true);

        try {
            const response = await promptService.getJobSeekerPrompts();
            const promptsData: any = response.data;
            setPrompts(promptsData);

            if (promptsData?.length > 0) {
                // If we have prompts, check if we already have a selected ID check if it's still valid
                const currentSelected = promptsData.find((p: any) => p._id === selectedPromptId);

                if (currentSelected) {
                    setPrompt(currentSelected.prompt);
                    setPromptSlug(currentSelected.braintrust_slug);
                } else {
                    // Default to first
                    const first = promptsData[0];
                    setSelectedPromptId(first._id);
                    setPrompt(first.prompt);
                    setPromptSlug(first.braintrust_slug);
                }
            } else {
                setPrompt('');
                setSelectedPromptId('');
                setPromptSlug('');
            }

        } catch (err: any) {
            setPromptsError(err?.response?.data?.message || 'Failed to fetch prompts');
            console.error('Error fetching prompts:', err);
        } finally {
            setPromptsLoading(false);
        }
    };

    const handleSyncBraintrust = async () => {
        setSyncLoading(true);
        try {
            await promptService.updatePromptsJobSeeker();
            await fetchJobSeekerPrompts();
            showSnackbar('Prompts synced with Braintrust successfully', 'success');
        } catch (err) {
            console.error('Error syncing with braintrust:', err);
            showSnackbar('Failed to sync with Braintrust', 'error');
        } finally {
            setSyncLoading(false);
        }
    };

    // Handle reset prompt
    const handleResetPrompt = async () => {
        try {
            if (selectedPromptId) {
                await promptService.getResetPrompt(selectedPromptId);
                fetchJobSeekerPrompts();
                showSnackbar('Prompt reset successfully', 'success');
            }
        } catch (err: any) {
            console.error('Error resetting prompt:', err);
            showSnackbar('Failed to reset prompt', 'error');
        }
    };

    // Variable definitions for different prompt types
    const getVariablesForType = (type: string) => {
        switch (type) {
            case 'welcomeagent-75cd':
                return [
                    { name: 'Candidate Name', value: '{{candidate_name}}' },
                ];

            case 'generate_resume_summary':
                return [
                    { name: 'Resume', value: '{{resume}}' },
                ];

            case 'resume_upload_cv_prompt':
                return [
                    { name: 'Candidate Name', value: '{{name}}' },
                    { name: 'Candidate Email', value: '{{email}}' },
                ];

            case 'job_seeker_ai_manager_prompt_for_application_feedback':
                return [
                    { name: 'Job Applications', value: '{{job_application}}' },
                ];

            case 'edit_resume_manager_prompt':
                return [
                    { name: 'Candidate Name', value: '{{name}}' },
                    { name: 'Resume Data', value: '{{resume_data}}' },
                ];

            case 'job_summary_prompt':
                return [
                    { name: 'Candidate Name', value: '{{name}}' },
                    { name: 'Resume Data', value: '{{resume_data}}' },
                    { name: 'Job Criteria Requirement', value: '{{jobData}}' },
                ];

            case 'resume_manager_prompt':
                return [
                    { name: 'Candidate Name', value: '{{name}}' },
                ];

            case 'job_seeker_ai_manager_prompt': // slug for AI Manager
                return [
                    { name: 'Jobseeker resume', value: '{{jobseeker_resume}}' },
                    { name: 'Job application', value: '{{job_application}}' },
                    { name: 'Application summary', value: '{{application_summary}}' },
                ];

            case 'job_profile_chat_prompt':
                return [
                    { name: 'Candidate Name', value: '{{candidate}}' },
                    { name: 'Resume Data', value: '{{resume_data}}' },
                ];

            case 'edit_job_profile_manager_prompt':
                return [
                    { name: 'Candidate Name', value: '{{candidate}}' },
                    { name: 'Current Job Profile', value: '{{current_jobprofile}}' },
                ];

            case 'job_seeker_homepage_ai_manager_prompt':
                return [];

            case 'generate_resume_jobprofile_summary':
                return [{ name: 'Chat', value: '{{chat}}' }];

            // ---------------------------------------
            case 'job_profile_summary':
                return [
                    { name: 'Candidate data', value: '{{candidate}}' },
                    { name: 'Project title', value: '{{project_title}}' },
                    { name: 'Description', value: '{{description}}' },
                    { name: 'Must have skills', value: '{{must_have_skills}}' },
                    { name: 'Good to have skills', value: '{{good_to_have_skills}}' },
                    { name: 'Qualifications', value: '{{qualifications}}' },
                    { name: 'Location', value: '{{location}}' },
                    { name: 'Assessment Criteria', value: '{{assessment_criteria}}' }
                ];

            default:
                return [];
        }
    };

    const handleVariableMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
        setVariableAnchorEl(event.currentTarget);
    };

    const handleVariableMenuClose = () => {
        setVariableAnchorEl(null);
    };

    const insertVariable = (variable: string) => {
        if (textFieldRef.current) {
            const input = textFieldRef.current;
            const start = input.selectionStart || 0;
            const end = input.selectionEnd || 0;
            const currentValue = input.value;

            const newValue = currentValue.substring(0, start) + variable + currentValue.substring(end);
            setPrompt(newValue);

            setTimeout(() => {
                input.focus();
                input.setSelectionRange(start + variable.length, start + variable.length);
            }, 0);
        }
        handleVariableMenuClose();
    };

    function a11yProps(index: number) {
        return {
            id: `vertical-tab-${index}`,
            'aria-controls': `vertical-tabpanel-${index}`,
        };
    }

    // Snackbar state
    const [snackbarOpen, setSnackbarOpen] = useState(false);
    const [snackbarMessage, setSnackbarMessage] = useState('');
    const [snackbarSeverity, setSnackbarSeverity] = useState<'success' | 'error' | 'info' | 'warning'>('success');

    const handleCloseSnackbar = () => {
        setSnackbarOpen(false);
    };

    const showSnackbar = (message: string, severity: 'success' | 'error' | 'info' | 'warning' = 'success') => {
        setSnackbarMessage(message);
        setSnackbarSeverity(severity);
        setSnackbarOpen(true);
    };


    // Render Prompt Tab Content
    const renderPromptTab = () => {
        if (promptsLoading && prompts.length === 0) {
            return (
                <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                    <CircularProgress />
                </Box>
            );
        }

        if (promptsError) {
            return (
                <Alert severity="error" sx={{ mt: 2 }}>
                    {promptsError}
                </Alert>
            );
        }

        return (
            <Box sx={{ mt: 2 }}>
                <Card
                    elevation={2}
                    sx={{
                        borderRadius: 2,
                        border: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
                        mb: 3
                    }}
                >
                    <CardContent sx={{ p: 3 }}>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
                            <Typography variant="h6" fontWeight="bold" color="primary">
                                Job Seeker AI Prompts
                            </Typography>
                            <Button
                                variant="outlined"
                                size="small"
                                startIcon={<SyncIcon />}
                                onClick={handleSyncBraintrust}
                                disabled={syncLoading}
                                sx={{ textTransform: 'none' }}
                            >
                                {syncLoading ? <CircularProgress size={20} /> : 'Sync with Braintrust'}
                            </Button>
                        </Box>
                        <Divider sx={{ mb: 2 }} />

                        <Box sx={{ display: 'flex', gap: 2, alignItems: 'flex-start' }}>
                            <Tabs
                                orientation="vertical"
                                variant="scrollable"
                                value={selectedPromptId}
                                onChange={handleChangePromptTab}
                                aria-label="Job Seeker Prompt tabs"
                                sx={{ borderRight: 1, borderColor: 'divider', minWidth: 200, height: 600 }}
                            >
                                {prompts.map((p: any) => (
                                    <Tab
                                        key={p._id}
                                        value={p._id}
                                        label={<div style={{ textTransform: "capitalize", textAlign: 'left', width: '100%' }}>{p.title}</div>}
                                    />
                                ))}
                            </Tabs>

                            <Box sx={{ flexGrow: 1 }}>
                                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                                    <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: 10 }}>
                                        <Typography variant="body2" color="textSecondary">
                                            Enter a prompt to be used. <b>(Braintrust Slug: {promptSlug})</b>
                                        </Typography>
                                        {getVariablesForType(promptSlug).length > 0 && (
                                            <Button
                                                size="small"
                                                variant="outlined"
                                                startIcon={<VariableIcon />}
                                                endIcon={<ArrowDownIcon />}
                                                onClick={(e) => handleVariableMenuOpen(e)}
                                                sx={{
                                                    // position: 'absolute',
                                                    // top: -55,
                                                    // right: 220, // Position next to other buttons or adjust as needed
                                                    minWidth: 'auto',
                                                    marginRight: 10,
                                                    px: 1,
                                                    backgroundColor: 'background.paper',
                                                    border: `1px solid ${theme.palette.divider}`,
                                                    '&:hover': {
                                                        backgroundColor: 'action.hover',
                                                    }
                                                }}
                                            >
                                                Variables
                                            </Button>
                                        )}
                                    </div>

                                    <Box sx={{ display: 'flex', gap: 1 }}>
                                        <Button
                                            variant="contained"
                                            color="primary"
                                            startIcon={<SendIcon />}
                                            onClick={handlePromptSubmit}
                                            disabled={!prompt?.trim() || promptLoading}
                                            sx={{
                                                minWidth: 100,
                                                boxShadow: `0 4px 8px ${alpha(theme.palette.primary.main, 0.2)}`,
                                            }}
                                        >
                                            {promptLoading ? <CircularProgress size={24} /> : 'Save'}
                                        </Button>
                                    </Box>
                                </Box>

                                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                                    <Box sx={{ position: 'relative', flexGrow: 1 }}>
                                        <TextField
                                            fullWidth
                                            multiline
                                            rows={20}
                                            placeholder="Enter your prompt here..."
                                            value={prompt}
                                            onChange={(e: any) => setPrompt(e.target.value)}
                                            disabled={promptLoading}
                                            inputRef={textFieldRef}
                                            sx={{
                                                '& .MuiInputBase-inputMultiline': {
                                                    resize: 'vertical',
                                                },
                                            }}
                                        />
                                    </Box>
                                </Box>
                            </Box>
                        </Box>
                    </CardContent>
                </Card>
            </Box>
        );
    };

    return (
        <Box>
            {/* Header with back button */}
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
                <IconButton
                    onClick={() => navigate(-1)}
                    sx={{ mr: 1 }}
                    aria-label="Back"
                >
                    <ArrowBackIcon />
                </IconButton>
                <Typography variant="h5" component="h1" fontWeight="bold" sx={{ flexGrow: 1 }}>
                    Job Seeker Prompts
                </Typography>
            </Box>

            {/* Prompt Tab Content */}
            {renderPromptTab()}

            {/* Variable Insertion Menu */}
            <Menu
                anchorEl={variableAnchorEl}
                open={Boolean(variableAnchorEl)}
                onClose={handleVariableMenuClose}
                anchorOrigin={{
                    vertical: 'bottom',
                    horizontal: 'left',
                }}
                transformOrigin={{
                    vertical: 'top',
                    horizontal: 'left',
                }}
            >
                {getVariablesForType(promptSlug)?.length && getVariablesForType(promptSlug).map((variable, index) => (
                    <MenuItem key={index} onClick={() => insertVariable(variable.value)}>
                        <ListItemIcon>
                            <VariableIcon fontSize="small" />
                        </ListItemIcon>
                        <ListItemText primary={variable.name} secondary={variable.value} />
                    </MenuItem>
                ))}
            </Menu>
        </Box>
    );
};

export default Prompts;
